import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import axios from 'axios';
import {Button} from 'primereact/button';
import {Calendar} from 'primereact/calendar';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from 'primereact/inputnumber';
import {InputText} from 'primereact/inputtext';
import {InputTextarea} from 'primereact/inputtextarea';
import {ProgressBar} from 'primereact/progressbar';
import CrmTimelineComponent from '../CrmTimelineComponent';
import SalesDocumentChainComponent from './SalesDocumentChainComponent';

const CURRENCIES = ['HUF', 'EUR', 'USD', 'GBP'];

const DealFormComponent = props => {
  const {t} = props;
  const history = useHistory();
  const {entity, id} = useParams();
  const dealId = props.dealId || window.App.urlVars().dealId || id;
  const editing = Boolean(props.dealId || (id && entity === 'deal') || (window.App.urlVars().update === 'deal' && window.App.urlVars().dealId));
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [formData, setFormData] = useState({pipeline: props.initialPipelineId || null, stage: null, currency: 'HUF', partner: window.App.urlVars().partnerId ? Number(window.App.urlVars().partnerId) : null});

  const headers = useMemo(() => ({
    Authorization: 'Bearer ' + localStorage.getItem('userToken'),
    'App-Network': localStorage.getItem('networkId')
  }), []);

  useEffect(() => {
    const requests = [
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/partner?item_per_page=200&sort=name&direction=asc', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/employee?item_per_page=200&sort=name&direction=asc', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-pipeline?item_per_page=200', {headers})
    ];
    if (editing) requests.push(axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-deal/' + dealId, {headers}));
    setLoading(true);
    Promise.all(requests)
      .then(responses => {
        setPartners(responses[0].data.data.items || []);
        setEmployees(responses[1].data.data.items || []);
        const availablePipelines = (responses[2].data.data.items || []).filter(item => item.active);
        setPipelines(availablePipelines);
        if (responses[3]) {
          const deal = responses[3].data.data;
          setFormData({
            ...deal,
            pipeline: deal.pipeline?.id || deal.pipeline || null,
            stage: deal.stage?.id || deal.stage || null,
            partner: deal.partner?.id || deal.partner || null,
            owner: deal.owner?.id || deal.owner || null,
            expectedCloseDate: deal.expected_close_date ? new Date(deal.expected_close_date) : null,
            nextActionAt: deal.next_action_at ? new Date(deal.next_action_at) : null
          });
        } else {
          const selectedPipeline = availablePipelines.find(item => Number(item.id) === Number(props.initialPipelineId)) || availablePipelines.find(item => item.default) || availablePipelines[0];
          const initialStage = selectedPipeline?.stages?.find(item => item.active && item.category === 'OPEN');
          setFormData(current => ({...current, pipeline: selectedPipeline?.id || null, stage: initialStage?.id || null, probability: initialStage?.probability ?? null}));
        }
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [dealId, editing, headers, t]);

  const set = (field, value) => setFormData(current => ({...current, [field]: value}));
  const dateOnly = value => value ? [value.getFullYear(), String(value.getMonth() + 1).padStart(2, '0'), String(value.getDate()).padStart(2, '0')].join('-') : null;
  const dateTime = value => value ? value.toISOString() : null;

  const save = goBack => {
    if (!String(formData.name || '').trim()) {
      window.App.toastShow('error', t('error'), t('fieldRequired'));
      return;
    }
    setLoading(true);
    const payload = {
      name: formData.name,
      partner: formData.partner || null,
      owner: formData.owner || null,
      pipeline: formData.pipeline,
      stage: formData.stage,
      amount: formData.amount ?? null,
      currency: formData.currency || 'HUF',
      probability: formData.probability ?? null,
      expectedCloseDate: dateOnly(formData.expectedCloseDate),
      nextActionAt: dateTime(formData.nextActionAt),
      note: formData.note || null,
      lostReason: formData.lostReason || null
    };
    const request = editing
      ? axios.patch(process.env.REACT_APP_API_HOST + '/api/v2/crm-deal/' + dealId, payload, {headers})
      : axios.post(process.env.REACT_APP_API_HOST + '/api/v2/crm-deal', payload, {headers});
    request.then(response => {
      window.App.toastShow('success', t('toastSaveSuccess'));
      window.App.setState({rerenderDealList: Math.random()});
      if (props.embedded) {
        props.onSaved?.(response.data.data);
      } else {
        history.push(goBack ? (props.backlink || '/crm/deal') : '/crm/deal/update/' + response.data.data.id);
      }
    }).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  };

  return <div className="deal-form widget crm-opportunity-form">
    {loading && <ProgressBar mode="indeterminate" style={{height: 5, position: 'absolute', top: 0, left: 0, borderRadius: 0}}/>}
    <div className="p-grid">
      <div className="p-col-12 p-lg-6"><div className="p-field p-fluid"><label>{t('fieldName')}</label><InputText value={formData.name || ''} onChange={event => set('name', event.target.value)}/></div></div>
      <div className="p-col-12 p-lg-3"><div className="p-field p-fluid"><label>{t('crmPipeline')}</label><Dropdown value={formData.pipeline} options={pipelines} optionLabel="name" optionValue="id" onChange={event => { const selected = pipelines.find(item => item.id === event.value); const first = selected?.stages?.find(item => item.active && item.category === 'OPEN'); setFormData(current => ({...current, pipeline: event.value, stage: first?.id || null, probability: first?.probability ?? current.probability})); }}/></div></div>
      <div className="p-col-12 p-lg-3"><div className="p-field p-fluid"><label>{t('crmOpportunityStage')}</label><Dropdown value={formData.stage} options={(pipelines.find(item => Number(item.id) === Number(formData.pipeline))?.stages || []).filter(item => item.active)} optionLabel="name" optionValue="id" onChange={event => {const stage = pipelines.flatMap(item => item.stages || []).find(item => item.id === event.value); setFormData(current => ({...current, stage: event.value, probability: stage?.probability ?? current.probability}));}}/></div></div>
      <div className="p-col-12 p-lg-6"><div className="p-field p-fluid"><label>{t('fieldPartner')}</label><Dropdown filter showClear value={formData.partner || null} options={partners} optionLabel="name" optionValue="id" onChange={event => set('partner', event.value)} placeholder={t('crmNoPartner')}/></div></div>
      <div className="p-col-12 p-lg-6"><div className="p-field p-fluid"><label>{t('crmOwner')}</label><Dropdown filter showClear value={formData.owner || null} options={employees} optionLabel="name" optionValue="id" onChange={event => set('owner', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('proformaAmount')}</label><InputNumber value={formData.amount != null ? Number(formData.amount) : null} mode="decimal" minFractionDigits={formData.currency === 'HUF' ? 0 : 2} maxFractionDigits={2} onValueChange={event => set('amount', event.value)}/></div></div>
      <div className="p-col-12 p-lg-2"><div className="p-field p-fluid"><label>{t('fieldCurrency')}</label><Dropdown value={formData.currency} options={CURRENCIES} onChange={event => set('currency', event.value)}/></div></div>
      <div className="p-col-12 p-lg-2"><div className="p-field p-fluid"><label>{t('crmProbability')}</label><InputNumber value={formData.probability} min={0} max={100} suffix=" %" onValueChange={event => set('probability', event.value)}/></div></div>
      <div className="p-col-12 p-lg-4"><div className="p-field p-fluid"><label>{t('crmExpectedCloseDate')}</label><Calendar value={formData.expectedCloseDate || null} dateFormat="yy-mm-dd" showIcon onChange={event => set('expectedCloseDate', event.value)}/></div></div>
      <div className="p-col-12 p-lg-6"><div className="p-field p-fluid"><label>{t('crmNextStep')}</label><Calendar value={formData.nextActionAt || null} dateFormat="yy-mm-dd" showTime showIcon onChange={event => set('nextActionAt', event.value)}/></div></div>
      <div className="p-col-12 p-lg-6"><div className="p-field p-fluid"><label>{t('crmLostReason')}</label><InputTextarea rows={2} disabled={pipelines.flatMap(item => item.stages || []).find(item => Number(item.id) === Number(formData.stage))?.category !== 'LOST'} value={formData.lostReason || ''} onChange={event => set('lostReason', event.target.value)}/></div></div>
      <div className="p-col-12"><div className="p-field p-fluid"><label>{t('fieldNote')}</label><InputTextarea rows={4} value={formData.note || ''} onChange={event => set('note', event.target.value)}/></div></div>
    </div>
    <div className="p-d-flex p-jc-end p-mt-3">
      <Button type="button" label={t('save')} icon="pi pi-save" loading={loading} className="p-button-success" onClick={() => save(false)}/>
      {!props.embedded && <Button type="button" label={t('saveAndBack')} icon="pi pi-arrow-left" loading={loading} className="p-button-success p-ml-2" onClick={() => save(true)}/>}
    </div>
    {editing && <SalesDocumentChainComponent dealId={dealId}/>}
    {editing && <div className="p-mt-4"><h3>{t('crmHistory')}</h3><CrmTimelineComponent dealId={dealId}/></div>}
  </div>;
};

export default withTranslation('common')(DealFormComponent);
